import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { LitPanel } from "@/components/ui/LitPanel";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { TextLink } from "@/components/ui/Button";
import { useContent } from "@/data/content";
import { formatEuro, usePricing } from "@/data/pricing";
import type { ServiceItem } from "@/data/content.types";
import { useTranslation } from "react-i18next";
import type { AppLocale } from "@/i18n/constants";

function ServicePanel({
  service,
  featured,
  delay,
  extra,
  price,
}: {
  service: ServiceItem;
  featured?: boolean;
  delay: number;
  extra?: React.ReactNode;
  /** Starting price shown next to the rung, e.g. "desde 249 € / mes". */
  price?: string;
}) {
  const Icon = service.icon;
  return (
    <LitPanel
      as="article"
      className={`flex h-full flex-col p-7 sm:p-9 ${featured ? "lg:row-span-2 lg:p-12" : ""}`}
    >
      <div data-reveal="" style={{ ["--reveal-delay" as string]: `${delay}ms` }} className="flex h-full flex-col">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="grid size-12 place-items-center rounded-2xl bg-teal-soft text-teal-strong">
            <Icon className="size-6" strokeWidth={1.75} aria-hidden="true" />
          </span>
          {service.rung ? (
            <span className="rounded-full border border-brand-primary/12 bg-white px-4 py-1.5 text-sm text-ink-soft">
              <span className="font-semibold text-brand-primary">{service.rung}</span>
              {price ? <> · {price}</> : null}
            </span>
          ) : null}
        </div>
        <h3
          className={`mt-8 font-display font-semibold leading-tight tracking-[-0.015em] text-brand-primary ${
            featured ? "text-[clamp(1.9rem,3.2vw,2.75rem)]" : "text-2xl"
          }`}
        >
          <Link to={service.href ?? "/"} className="after:absolute after:inset-0 after:content-['']">
            {service.title}
          </Link>
        </h3>
        <p className={`mt-4 leading-relaxed text-ink-soft ${featured ? "text-lg" : ""}`}>{service.desc}</p>
        {service.features && service.features.length > 0 ? (
          <ul className={`mt-6 space-y-2.5 ${featured ? "" : "text-base"}`}>
            {service.features.map((feature) => (
              <li key={feature} className="node text-ink">
                {feature}
              </li>
            ))}
          </ul>
        ) : null}
        {extra}
        <span className="mt-auto inline-flex items-center gap-1.5 pt-8 font-semibold text-brand-primary">
          {service.ctaLabel}
          <ArrowUpRight
            aria-hidden="true"
            className="size-4 transition-transform duration-300 group-hover/panel:-translate-y-0.5 group-hover/panel:translate-x-0.5"
          />
        </span>
      </div>
    </LitPanel>
  );
}

export function ServicesSection() {
  const { services, home } = useContent();
  const pricing = usePricing();
  const { i18n } = useTranslation();
  const locale = (i18n.language === "en" ? "en" : "es") as AppLocale;
  const [featured, ...rest] = services;
  const branding = pricing.addOns.items[0];

  /* Each rung names what it costs to start, straight from the price list. */
  const monthly = pricing.packs.filter((pack) => pack.cadence === "month");
  const priceFor = (href?: string): string | undefined => {
    if (href === "/gestion-instagram" && monthly.length) {
      const min = Math.min(...monthly.map((pack) => pack.price));
      return `${pricing.from} ${formatEuro(min, locale)} ${pricing.perMonth}`;
    }
    const item = pricing.services.items.find((entry) => entry.href === href);
    if (!item) return undefined;
    return `${item.from ? `${pricing.from} ` : ""}${formatEuro(item.price, locale)}`;
  };

  return (
    <section id="servicios" aria-labelledby="services-heading" className="bg-brand-bg">
      <div className="mx-auto max-w-7xl px-4 py-24 sm:px-6 md:py-32 lg:px-8">
        <SectionHeader id="services-heading" title={home.services.title} lead={home.services.subtitle} />

        <div className="mt-14 grid gap-6 lg:grid-cols-[1.15fr_1fr] lg:grid-rows-2">
          {featured ? (
            <ServicePanel
              service={featured}
              featured
              delay={0}
              price={priceFor(featured.href)}
              extra={
                <dl className="mt-8 border-t border-brand-primary/10 pt-6">
                  {pricing.packs.map((pack) => (
                    <div key={pack.id} className="flex items-baseline gap-3 py-2">
                      <dt className="shrink-0 font-display text-lg font-semibold text-brand-primary">{pack.name}</dt>
                      <span aria-hidden="true" className="flex-1 border-b-2 border-dotted border-brand-primary/15" />
                      <dd className="shrink-0 text-ink-soft">
                        <span className="font-semibold text-brand-primary">{formatEuro(pack.price, locale)}</span>{" "}
                        {pack.cadence === "month" ? pricing.perMonth : pricing.oneOff}
                      </dd>
                    </div>
                  ))}
                </dl>
              }
            />
          ) : null}
          {rest.map((service, i) => (
            <ServicePanel
              key={service.href ?? service.title}
              service={service}
              delay={(i + 1) * 90}
              price={priceFor(service.href)}
            />
          ))}
        </div>

        <div
          data-reveal=""
          className="mt-10 flex flex-col gap-4 rounded-[1.75rem] border border-dashed border-brand-primary/20 px-7 py-6 sm:flex-row sm:items-center sm:justify-between"
        >
          <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:gap-6">
            <p className="font-semibold text-brand-primary">{home.services.addOnsTitle}</p>
            <ul className="flex flex-col gap-2 text-ink-soft sm:flex-row sm:flex-wrap sm:gap-x-6">
              <li className="node">
                {branding.title} <span className="text-ink-muted">· {branding.by}</span>
              </li>
              <li className="node">
                {pricing.rateCard.title}{" "}
                <span className="text-ink-muted">
                  · {pricing.from} {formatEuro(5, locale)}
                </span>
              </li>
            </ul>
          </div>
          <TextLink to="/precios#sueltos" className="shrink-0">
            {pricing.rateCard.title}
          </TextLink>
        </div>
      </div>
    </section>
  );
}
